<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="mobile-registration-form__username-password-container">
    <h3 class="mobile-registration-form__subtitle">Create your username and password:</h3>
    <fieldset v-show="formFields.registrationUserName && !formFields.registrationUserName.isHidden"
              id="mobileUserNameField"
              class="wk-js2 mobile-registration-form-field">
      <UtdTextInput label="Username"
                    :is-error="checkFieldInvalid(REG_FIELD.USER_NAME) && isDirty.registrationUserName"
                    :error-message="$t(userNameErrorMessage())"
                    :required="formFields.registrationUserName?.required">
        <input id="mobileRegistrationUserName"
               v-model="form.registrationUserName"
               aria-label="Username"
               :maxlength="formFields.registrationUserName?.maxLength"
               type="text"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="username"
               @input="checkResetServerError(REG_FIELD.USER_NAME)"
               @blur="handleUserNameBlur">
      </UtdTextInput>
    </fieldset>
    <fieldset id="mobilePasswordField"
              class="wk-js2 mobile-registration-form-field">
      <UtdPasswordInput :label="$t('LOGIN.PASSWORD')"
                        :is-error="checkFieldInvalid(REG_FIELD.PASSWORD) && isDirty.password"
                        :error-message="$t(passwordErrorMessage())"
                        :required="formFields.password?.required"
                        @text-input-toggle-show="togglePasswordVisibleInput">
        <input id="mobilePassword"
               v-model="form.password"
               :type="newPasswordInputType"
               :maxlength="formFields.password?.maxLength"
               aria-label="Password"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="new-password"
               @input="checkResetServerError(REG_FIELD.PASSWORD)"
               @blur="() => isDirty.password = true">
      </UtdPasswordInput>
    </fieldset>
    <fieldset class="mobile-registration-form__password-rules">
      <h4>Password rules:</h4>
      <PasswordRules class="ds1-ml-1" />
    </fieldset>
    <fieldset id="mobileVerifyPasswordField"
              class="wk-js2 mobile-registration-form-field">
      <UtdPasswordInput label="Verify Password"
                        :is-error="checkFieldInvalid(REG_FIELD.VERIFY_PASSWORD) && isDirty.verifyPassword"
                        :error-message="$t(getFieldErrorMessage(REG_FIELD.VERIFY_PASSWORD))"
                        :required="formFields.verifyPassword?.required"
                        @text-input-toggle-show="toggleVerifyPasswordVisibleInput">
        <input id="mobileVerifyPassword"
               v-model="form.verifyPassword"
               :type="verifyPasswordInputType"
               :maxlength="formFields.verifyPassword?.maxLength"
               aria-label="Verify Password"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="new-password"
               @input="checkResetServerError(REG_FIELD.VERIFY_PASSWORD)"
               @blur="() => isDirty.verifyPassword = true">
      </UtdPasswordInput>
    </fieldset>
  </div>
</template>

<script setup>
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdPasswordInput from '_acaSrc/components/shared/input/UtdPasswordInput.vue';
import PasswordRules from '_acaSrc/components/login/PasswordRules.vue';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import { useStore } from 'vuex';
import { ref, computed } from 'vue';
import { REG_FIELD, REGISTRATION_FIELD_ERROR } from '_acaSrc/utility/constants';

const store = useStore();

const registrationForm = computed(() => store.getters['login/registrationForm']);
const formFields = computed(() => registrationForm.value.formFields);

defineProps({
    hasSentVerificationCode: { type: Boolean, default: false },
    hasResentVerificationCode: { type: Boolean, default: false },
    isAuthNotificationVisible: { type: Boolean, default: false },
    isAuthErrorNotificationVisible: { type: Boolean, default: false },
    isAllowOtcGeneration: { type: Boolean, default: true },
    isSubmitting: { type: Boolean, default: false }
});

const newPasswordInputType = ref('password');
const verifyPasswordInputType = ref('password');

const togglePasswordVisibleInput = (hideText) => {
    newPasswordInputType.value = hideText ? 'password' : 'text';
};

const toggleVerifyPasswordVisibleInput = (hideText) => {
    verifyPasswordInputType.value = hideText ? 'password' : 'text';
};

const {
    form,
    isDirty,
    checkResetServerError,
    checkFieldInvalid,
    getFieldErrorMessage,
    userNameErrorMessage,
    passwordErrorMessage,
    setServerError
} = useRegistrationForm();

const handleUserNameBlur = async() => {
    isDirty.registrationUserName = true;
    if (checkFieldInvalid(REG_FIELD.USER_NAME)) {
        return;
    }
    try {
        const response = await utdRest('register/checkUserName', {
            userName: form.registrationUserName
        });
        if (response?.value) {
            setServerError(REG_FIELD.USER_NAME, REGISTRATION_FIELD_ERROR.USERNAME_TAKEN);
        }
    }
    catch (error) {
        setServerError(REG_FIELD.USER_NAME, REGISTRATION_FIELD_ERROR.USERNAME_CHECK_SERVICE_UNAVAILABLE);
    }
    finally {
        checkResetServerError();
    }
};
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
@DS1-UTD-ERROR-ON-BLUE-BG-COLOR: @WKCE-RED-SHADE1;



.mobile-registration-form__password-rules {
  .ds1-mb-2();
  border: none;
}

.mobile-registration-form__username-password-container {
  .ds1-mt-3();
}

.xAIMobile {
  .mobile-registration-form__username-password-container {
    :deep(.wk-field-input) {
      background-color: @WKCE-WHITE;
      border-radius: 50px;
      border: 1px solid @WKCE-CREAM;
      font-size: 16px;
      font-weight: 300;
    }

    :deep(.wk-field-label) {
      color: @WKCE-CREAM-SHADE2;
      font-size: 14px;
      font-weight: 600;
    }

    :deep(.show-password-toggle) {
      color: @WKCE-CREAM-SHADE2;
    }

    :deep(.utd-password-input__toggle) {
      color: @WKCE-CREAM-SHADE2;
    }
  }
}
</style>

